import { Editor } from '../core/Editor';
import { BlendMode } from '../types';
import { Icons } from './Icons';

export class LayersPanel {
  private editor: Editor;
  private container: HTMLElement;

  constructor(editor: Editor, container: HTMLElement) {
    this.editor = editor;
    this.container = container;
  }

  public render() {
    const activeLayer = this.editor.getActiveLayer();
    const layers = [...this.editor.layers].reverse(); // display top layer first

    this.container.innerHTML = `
      <div class="panel-card layers-card">
        <div class="panel-header">
          <div class="panel-title">
            ${Icons.layers}
            <span>Layers (${this.editor.layers.length})</span>
          </div>
          <div class="panel-header-actions">
            <button class="icon-btn-mini" id="btn-toggle-transparent" title="Toggle Transparent Canvas Checkerboard">
              <span class="checker-mini"></span>
            </button>
          </div>
        </div>

        <!-- Layer Blend Mode & Opacity -->
        <div class="layer-properties-bar">
          <select id="layer-blend-mode" class="styled-select mini" ${!activeLayer ? 'disabled' : ''}>
            <option value="source-over" ${activeLayer?.blendMode === 'source-over' ? 'selected' : ''}>Normal</option>
            <option value="multiply" ${activeLayer?.blendMode === 'multiply' ? 'selected' : ''}>Multiply</option>
            <option value="screen" ${activeLayer?.blendMode === 'screen' ? 'selected' : ''}>Screen</option>
            <option value="overlay" ${activeLayer?.blendMode === 'overlay' ? 'selected' : ''}>Overlay</option>
            <option value="darken" ${activeLayer?.blendMode === 'darken' ? 'selected' : ''}>Darken</option>
            <option value="lighten" ${activeLayer?.blendMode === 'lighten' ? 'selected' : ''}>Lighten</option>
            <option value="color-dodge" ${activeLayer?.blendMode === 'color-dodge' ? 'selected' : ''}>Color Dodge</option>
            <option value="color-burn" ${activeLayer?.blendMode === 'color-burn' ? 'selected' : ''}>Color Burn</option>
            <option value="difference" ${activeLayer?.blendMode === 'difference' ? 'selected' : ''}>Difference</option>
            <option value="exclusion" ${activeLayer?.blendMode === 'exclusion' ? 'selected' : ''}>Exclusion</option>
          </select>

          <div class="opacity-control">
            <span class="prop-lbl">Opac:</span>
            <input type="range" id="layer-opacity" min="0" max="100" value="${activeLayer ? Math.round(activeLayer.opacity * 100) : 100}" class="styled-range mini" ${!activeLayer ? 'disabled' : ''} />
            <span class="opacity-val" id="layer-opacity-val">${activeLayer ? Math.round(activeLayer.opacity * 100) : 100}%</span>
          </div>
        </div>

        <!-- Layers List -->
        <div class="layers-list-scroll" id="layers-list">
          ${layers.length === 0
            ? `<div class="empty-layers-hint">No document open.<br/>Drop a photo or click Open to begin.</div>`
            : layers
                .map(layer => {
                  const isActive = activeLayer?.id === layer.id;
                  const thumb = layer.getThumbnail();
                  return `
              <div class="layer-item ${isActive ? 'active' : ''} ${layer.locked ? 'locked' : ''} ${!layer.visible ? 'hidden' : ''}" 
                   data-id="${layer.id}">
                <!-- Visibility Eye -->
                <button type="button" class="layer-eye-btn" data-id="${layer.id}" title="Toggle Visibility">
                  ${layer.visible ? Icons.eye : Icons.eyeOff}
                </button>

                <!-- Thumbnail -->
                <div class="layer-thumb-box">
                  <img src="${thumb}" alt="${layer.name}" class="layer-thumb-img" />
                  ${layer.type === 'text' ? '<span class="type-badge">T</span>' : ''}
                </div>

                <!-- Layer Name -->
                <div class="layer-name-box">
                  <span class="layer-name-text" title="Double click to rename">${layer.name}</span>
                </div>

                <!-- Lock Toggle -->
                <button type="button" class="layer-lock-btn" data-id="${layer.id}" title="Lock Layer">
                  ${layer.locked ? Icons.lock : Icons.unlock}
                </button>
              </div>
            `;
            })
            .join('')}
        </div>

        <!-- Layer Actions Footer -->
        <div class="layers-footer">
          <button type="button" class="footer-btn" id="btn-add-layer" title="New Layer">
            ${Icons.plus}
          </button>
          <button type="button" class="footer-btn" id="btn-dup-layer" title="Duplicate Layer">
            ${Icons.copy}
          </button>
          <button type="button" class="footer-btn" id="btn-merge-layer" title="Merge Down">
            ${Icons.merge}
          </button>
          <button type="button" class="footer-btn danger" id="btn-del-layer" title="Delete Layer">
            ${Icons.trash}
          </button>
        </div>
      </div>
    `;

    this.attachEvents();
  }

  private attachEvents() {
    // Select layer
    this.container.querySelectorAll('.layer-item').forEach(item => {
      item.addEventListener('click', (e) => {
        // If clicked on eye or lock, don't trigger layer select
        if ((e.target as HTMLElement).closest('.layer-eye-btn') || (e.target as HTMLElement).closest('.layer-lock-btn')) {
          return;
        }
        const id = (item as HTMLElement).dataset.id;
        if (id) {
          this.editor.setActiveLayer(id);
          this.render();
        }
      });

      // Double-click to rename
      item.addEventListener('dblclick', () => {
        const id = (item as HTMLElement).dataset.id;
        const layer = this.editor.layers.find(l => l.id === id);
        if (layer) {
          const newName = window.prompt('Rename layer:', layer.name);
          if (newName && newName.trim()) {
            layer.name = newName.trim();
            this.render();
          }
        }
      });
    });

    // Visibility toggle
    this.container.querySelectorAll('.layer-eye-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        const layer = this.editor.layers.find(l => l.id === id);
        if (layer) {
          layer.visible = !layer.visible;
          this.editor.render();
          this.render();
        }
      });
    });

    // Lock toggle
    this.container.querySelectorAll('.layer-lock-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        const layer = this.editor.layers.find(l => l.id === id);
        if (layer) {
          layer.locked = !layer.locked;
          this.render();
        }
      });
    });

    // Blend mode
    const blendSelect = this.container.querySelector('#layer-blend-mode') as HTMLSelectElement;
    blendSelect?.addEventListener('change', () => {
      const active = this.editor.getActiveLayer();
      if (active) {
        active.blendMode = blendSelect.value as BlendMode;
        this.editor.recordHistory('Change Blend Mode');
        this.editor.render();
      }
    });

    // Opacity slider
    const opacSlider = this.container.querySelector('#layer-opacity') as HTMLInputElement;
    const opacVal = this.container.querySelector('#layer-opacity-val') as HTMLElement;
    opacSlider?.addEventListener('input', () => {
      const active = this.editor.getActiveLayer();
      if (active) {
        const val = parseInt(opacSlider.value);
        active.opacity = val / 100;
        if (opacVal) opacVal.textContent = `${val}%`;
        this.editor.render();
      }
    });
    opacSlider?.addEventListener('change', () => {
      this.editor.recordHistory('Change Layer Opacity');
    });

    // New layer
    this.container.querySelector('#btn-add-layer')?.addEventListener('click', () => {
      this.editor.addNewLayer();
      this.render();
    });

    // Duplicate layer
    this.container.querySelector('#btn-dup-layer')?.addEventListener('click', () => {
      this.editor.duplicateActiveLayer();
      this.render();
    });

    // Merge down
    this.container.querySelector('#btn-merge-layer')?.addEventListener('click', () => {
      this.editor.mergeDown();
      this.render();
    });

    // Delete layer
    this.container.querySelector('#btn-del-layer')?.addEventListener('click', () => {
      this.editor.deleteActiveLayer();
      this.render();
    });

    // Toggle transparent checkerboard
    this.container.querySelector('#btn-toggle-transparent')?.addEventListener('click', () => {
      this.editor.toggleTransparentBackground();
    });
  }
}
